import React, { ReactNode } from 'react';
import { clsx } from 'clsx';
import {
  ActionButton,
  Label,
  ValueText,
  ValueLink,
  ValueList,
  ActionLink,
  ValueButton,
} from '@/components/atoms/Section';
import LinkValueList from '@/components/atoms/ValueList';
import styles from './Grid.module.scss';
import { useTranslation } from 'react-i18next';

export type GridAction =
  | { kind: 'Button'; label: ReactNode; onClick: () => void; disabled?: boolean }
  | { kind: 'Link'; label: ReactNode; url: string; newTab?: boolean; disabled?: boolean; onClick?: () => void };

export type GridInfo = {
  label: string | JSX.Element;
  value: string | null;
  action?: GridAction;
  background?: string;
  color?: string;
  description?: string;
  dataCy?: string;
};

export type GridLink = { label: string; value: string; url: string; newTab?: boolean };

export type GridLinkButton = { label: string; url: string; newTab?: boolean };

export type GridHtml = { label: string; value: React.ReactElement | null; action?: GridAction };

export type GridList = { label: string; value: string[] | JSX.Element[] | null; action?: GridAction };

export type GridItem =
  | {
      kind: 'Text';
      info: GridInfo;
    }
  | {
      kind: 'TextEllipsis';
      info: GridInfo;
    }
  | {
      kind: 'LinkOrButton';
      info: GridInfo;
    }
  | {
      kind: 'Action';
      action: GridAction;
    }
  | {
      kind: 'Html';
      info: GridHtml;
    }
  | {
      kind: 'List';
      info: GridList;
    }
  | {
      kind: 'Custom';
      element: React.ReactElement;
    };

const View: React.FC<{ column: number; items: GridItem[] }> = ({ items, column = 5 }) => {
  const { t } = useTranslation();

  const style: React.CSSProperties = { gridTemplateColumns: `repeat(${column}, ${100 / column}%)` };
  const totalItem = items.length;
  const lastRowCount = totalItem % column === 0 ? column : totalItem % column;

  return (
    <div
      style={style}
      className={styles.container}
    >
      {items.map((item, index) => {
        // @TODO: please refactor this to use complete css instead
        const className = clsx(
          (index + 1) % column === 0 ? styles.noBorderRight : '',
          index >= totalItem - lastRowCount ? styles.noBorderBottom : '',
        );

        let labelInfo;

        switch (item.kind) {
          case 'Text':
          case 'TextEllipsis':
          case 'LinkOrButton':
          case 'Html':
          case 'List':
            labelInfo = item.info;
            break;

          default:
            break;
        }

        return (
          <div
            key={index}
            className={className}
          >
            <div className={styles.item}>
              <div className={styles.itemInner}>
                {item.kind === 'Custom' ? (
                  item.element
                ) : (
                  <>
                    {!!labelInfo &&
                      (typeof labelInfo.label === 'string' ? (
                        <Label labelInfo={{ ...labelInfo, label: labelInfo.label }} />
                      ) : (
                        labelInfo.label
                      ))}
                    <Item item={item} />
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface ItemProps {
  item: GridItem;
}
const Item: React.FC<ItemProps> = ({ item }) => {
  switch (item.kind) {
    case 'TextEllipsis':
    case 'Text':
      return (
        <>
          <ValueText
            label={item.info.value || '-'}
            kind={item.kind === 'Text' ? 'Text' : 'TextEllipsis'}
            background={item.info.background}
            color={item.info.color}
            dataCy={item.info.dataCy}
          />
          {item.info.action && renderAction(item.info.action)}
        </>
      );
    case 'LinkOrButton':
      return item.info.action ? renderValueAction(item.info.action) : null;
    case 'Action':
      return renderValueAction(item.action);
    case 'Html':
      return (
        <>
          {item.info.value}
          {!item.info.value && (
            <ValueText
              label={'-'}
              kind={'Text'}
            />
          )}
          {item.info.action && renderAction(item.info.action)}
        </>
      );
    case 'List':
      return (
        <>
          {!item.info.value || !item.info.value.length ? (
            <ValueText
              label={'-'}
              kind={'Text'}
            />
          ) : (
            <ValueList labels={item.info.value} />
          )}
          {item.info.action && renderAction(item.info.action)}
        </>
      );
    case 'Custom':
      return item.element;
  }
};

function renderAction(action: GridAction): React.ReactElement {
  if (action.kind === 'Button') {
    return (
      <div className={styles.itemAction}>
        <ActionButton
          label={action.label}
          onClick={action.onClick}
          disabled={action.disabled}
          dataCy={`cell-action`}
        />
      </div>
    );
  } else {
    return (
      <div className={styles.itemAction}>
        <ActionLink
          label={action.label}
          url={action.url}
          disabled={action.disabled}
          newTab={action.newTab}
          dataCy={`cell-action`}
        />
      </div>
    );
  }
}

function renderValueAction(action: GridAction): React.ReactElement {
  if (action.kind === 'Button') {
    return (
      <ValueButton
        label={action.label}
        onClick={action.onClick}
        disabled={action.disabled}
      />
    );
  } else {
    return (
      <ValueLink
        label={action.label}
        url={action.url}
        disabled={action.disabled}
        newTab={action.newTab}
      />
    );
  }
}

export function itemText(
  label: string | JSX.Element,
  value: number | string | null | undefined,
  action?: GridAction,
  background?: string,
  color?: string,
  description?: string,
  dataCy?: string,
): GridItem {
  return {
    kind: 'Text',
    info: { label, value: value ? String(value) : null, action, background, color, description, dataCy },
  };
}

export function itemTextEllipsis(
  label: string,
  value: string | null,
  action?: GridAction,
  background?: string,
): GridItem {
  return { kind: 'TextEllipsis', info: { label, value, action, background } };
}

export function itemLinkButton(label: string, action: GridAction): GridItem {
  return { kind: 'LinkOrButton', info: { label, value: null, action } };
}

export function itemAction(action: GridAction): GridItem {
  return { kind: 'Action', action };
}

export function itemList(label: string, value: string[] | JSX.Element[] | null, action?: GridAction): GridItem {
  return { kind: 'List', info: { label, value, action } };
}

export function itemHtml(label: string, value: React.ReactElement | null, action?: GridAction): GridItem {
  return { kind: 'Html', info: { label, value, action } };
}

export function itemCustom(element: React.ReactElement): GridItem {
  return { kind: 'Custom', element };
}

export function linkList({
  label,
  data,
  onClick,
}: {
  label: string;
  data?: string[];
  onClick?: (v: string) => void;
  disabled?: boolean;
}): GridItem {
  return itemCustom(
    <>
      <Label labelInfo={{ label }} />
      <LinkValueList
        values={data || []}
        onItemClick={onClick}
      />
    </>,
  );
}

export default View;
